import React from 'react';

import { Autocomplete, TextField } from '@mui/material';

import type { OpportunityRoomAsset } from '~/hooks/apis/offers/useGetOffer';

import type { InclusionAssetLink } from './types';

interface Props {
  currency: string;
  knownAssets: Array<OpportunityRoomAsset>;
  value: InclusionAssetLink;
  onChange: (value: InclusionAssetLink) => void;
}

// See docs/business-rules.md#salesforce-data-model (existing assets matched by id, never name).
export default function AssetLinkField({ currency, knownAssets, value, onChange }: Props) {
  const selectedAsset = knownAssets.find((asset) => asset.sfid === value.existingAssetSfId) ?? null;
  const currentLabel = value.mode === 'existing' ? (selectedAsset?.name ?? '') : (value.newAssetName ?? '');

  return (
    <Autocomplete
      size="small"
      freeSolo
      options={knownAssets}
      value={selectedAsset}
      inputValue={currentLabel}
      getOptionLabel={(asset) => (typeof asset === 'string' ? asset : (asset.name ?? asset.sfid ?? ''))}
      isOptionEqualToValue={(option, other) => option.sfid === other.sfid}
      onChange={(_event, asset) => {
        if (asset && typeof asset !== 'string') {
          onChange({
            mode: 'existing',
            existingAssetSfId: asset.sfid ?? undefined,
            existingAssetName: asset.name ?? undefined,
          });
        }
      }}
      onInputChange={(_event, inputValue, reason) => {
        if (reason === 'input') {
          onChange({ mode: 'new', newAssetName: inputValue });
        }
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Asset"
          helperText={value.mode === 'new' ? `Creates new asset (${currency})` : undefined}
        />
      )}
      sx={{ minWidth: 180, flex: 1 }}
    />
  );
}
